import { EntityAttributes, Option } from '@/constants/general';
import { portalAPI, portalURL } from '@/helpers/environmentHelper';
import { logToServer } from '@/services/LogService';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { store } from '@/state/store';
import {
    BalanceResponse,
    BankAccountsResponse,
    BlockchainNetworks,
    CountriesResponse,
    CurrenciesResponse,
    EntityResponse,
    InstrumentCurrenciesResponse,
    InstrumentResponse,
    LastEodResponse,
    LinkedEntitiesResponse,
    MarginRequirementsResponse,
    SSIProfileBankAccountsResponse,
    SSIProfileWalletsResponse,
    SummaryResponse,
    VaspResponse,
    WalletsResponse
} from '@/types/sso';
import axios from 'axios';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useSWR, { SWRConfiguration, SWRResponse } from 'swr';
import { Logger } from '../logger';

export type BalancesTypeView = 'OTC' | 'GB' | 'combined';

interface EntityType {
    isAgent: boolean;
    isMarginClient: boolean;
    isDeliverableClient: boolean;
    isExecutingBroker: boolean;
    isWhiteLabel: boolean;
    isGceEntity: boolean;
    isCustodian: boolean;
    isLP: boolean;
    isBank: boolean;
    isDirectClient: boolean;
}

export interface SsoContext {
    jwt?: string;
    entities?: any;
    entityType: EntityType;
    flattenEntities?: EntityResponse[];
    vaspApi: SWRResponse<VaspResponse[]>;
    currenciesApi: SWRResponse<CurrenciesResponse[]>;
    balancesApi: SWRResponse<BalanceResponse[]>;
    bankInfoApi: SWRResponse<BankAccountsResponse[]>;
    walletInfoApi: SWRResponse<WalletsResponse[]>;
    ssiProfileBankInfoApi: SWRResponse<SSIProfileBankAccountsResponse[]>;
    ssiProfileWalletInfoApi: SWRResponse<SSIProfileWalletsResponse[]>;
    instrumentsApi: SWRResponse<InstrumentResponse[]>;
    instrumentsIncludeRatioApi: SWRResponse<InstrumentResponse[]>;
    instrumentCurrenciesApi: SWRResponse<InstrumentCurrenciesResponse[]>;
    linkedEntitiesApi: SWRResponse<LinkedEntitiesResponse[]>;
    summaryApi: SWRResponse<SummaryResponse>;
    marginRequirementsApi: SWRResponse<MarginRequirementsResponse>;
    lastEODApi: SWRResponse<LastEodResponse>;
    blockchainsApi: SWRResponse<BlockchainNetworks[]>;
    countriesApi: SWRResponse<CountriesResponse[]>;
    summaryHistoricDate?: string;
    selectedBalancesDirection: BalancesTypeView;
    setSummaryHistoricDate: React.Dispatch<React.SetStateAction<string | undefined>>;
    setSelectedBalancesDirection: React.Dispatch<React.SetStateAction<BalancesTypeView>>;
    foundEntity?: EntityResponse;
    permissions: string[];
    isSSOEnabled: boolean;
    isLoadingSSO: SSOLoadingState;
    canRequestDeposit: boolean;
    canRequestWithdrawal: boolean;
    depositEnabled: boolean;
    withdrawalEnabled: boolean;
    hasPermission: (permission: string | string[], mode?: 'some' | 'all') => boolean;
    onFetchJWT: () => Promise<any>;
    accountType: Option;
    setAccountType: React.Dispatch<React.SetStateAction<Option>>;
    isSpecificAccountSelected: boolean;
}

export interface SSOProviderProps {
    children: React.ReactNode;
}

const depositEnabled = window.config.modules.deposit.enabled;
const withdrawalEnabled = window.config.modules.withdrawal.enabled;

export const fetchOnce = {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
};

export type SSOLoadingState = 'idle' | 'pending' | 'success' | 'error';

export const defaultAccountTypeOption: Option = {
    label: 'All Accounts',
    value: null
};

const SSOContext = createContext({} as SsoContext);
export const useSso = () => useContext(SSOContext);
export const SSOProvider = (props: SSOProviderProps) => {
    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);

    const [jwt, setJWT] = useState<string>();
    const [isLoadingSSO, setIsLoadingSSO] = useState<SSOLoadingState>('idle');
    const [summaryHistoricDate, setSummaryHistoricDate] = useState<string>();
    const [selectedBalancesDirection, setSelectedBalancesDirection] = useState<BalancesTypeView>('OTC');

    const [accountType, setAccountType] = useState<Option>(defaultAccountTypeOption);

    const isSpecificAccountSelected = useMemo(() => !!accountType?.value, [accountType]);

    const onFetchJWT = useCallback(async () => {
        if (credentials && !jwt) {
            setIsLoadingSSO('pending');
            try {
                if (portalURL) {
                    const response = await axios.post(portalAPI('/api/auth/authenticate-via-sso'), {
                        username: credentials?.username,
                        celer_auth_token: credentials?.authToken
                    });
                    setIsLoadingSSO('success');
                    setJWT(response.data.jwt);
                    logToServer('info', `User [${credentials?.username}] has authenticated via SSO`);
                    return response.data.jwt;
                }
            } catch (err: any) {
                setIsLoadingSSO('error');
                logToServer(
                    'info',
                    `User [${credentials?.username}] has failed to authenticate via SSO. Error: ${err?.message}`
                );
            }
        } else if (!credentials) {
            setJWT(undefined);
        }
    }, [credentials, jwt]);

    useEffect(() => {
        onFetchJWT();
    }, [credentials]);

    const entitiesApi = usePortalSWR<EntityResponse[]>('/api/entity/list', () =>
        fetchPortalAPI(onFetchJWT, '/api/entity/list', jwt)
    );

    const { data: entities, isLoading: isEntitiesLoading } = entitiesApi;

    const flattenEntities = useMemo(() => {
        return flattenEntityChildren(entities || []);
    }, [entities]);

    const foundEntity = useMemo(() => {
        return flattenEntities?.find((entity) =>
            entity.nameMappings?.find((name) => name.source === 'CELER' && name.name === currentAccount)
        );
    }, [flattenEntities, currentAccount]);

    const entityType = useMemo(
        () => ({
            isAgent: foundEntity?.attributes?.includes(EntityAttributes.agent) || false,
            isMarginClient: foundEntity?.attributes?.includes(EntityAttributes.margin) || false,
            isDeliverableClient: foundEntity?.attributes?.includes(EntityAttributes.deliverable) || false,
            isExecutingBroker: foundEntity?.attributes?.includes(EntityAttributes.executingBroker) || false,
            isWhiteLabel: foundEntity?.attributes?.includes(EntityAttributes.whitelabel) || false,
            isGceEntity: foundEntity?.attributes?.includes(EntityAttributes.gceEntity) || false,
            isCustodian: foundEntity?.attributes?.includes(EntityAttributes.custodian) || false,
            isLP: foundEntity?.attributes?.includes(EntityAttributes.liquidityProvider) || false,
            isBank: foundEntity?.attributes?.includes(EntityAttributes.bank) || false,
            isDirectClient: foundEntity?.attributes?.includes(EntityAttributes.transferReceiver) || false
        }),
        [foundEntity]
    );

    const currentEntity = useMemo(() => {
        return foundEntity;
    }, [foundEntity]);

    const hasPermission = useCallback(
        (permission: string | string[], mode: 'some' | 'all' = 'some') => {
            if (!foundEntity || !foundEntity.permissions) return false;
            else {
                if (typeof permission === 'object') {
                    if (mode === 'all') {
                        return permission.every((perm) => foundEntity.permissions.includes(perm));
                    } else if (mode === 'some') {
                        return permission.some((perm) => foundEntity.permissions.includes(perm));
                    } else return false;
                } else {
                    return foundEntity.permissions.includes(permission);
                }
            }
        },
        [foundEntity]
    );

    const isSSOEnabled = useMemo(() => !!jwt && !!foundEntity, [jwt, foundEntity]);

    const gb10Link = useMemo(() => {
        return currentEntity?.addresses?.find((address) => address.type === 'GB10Link');
    }, [currentEntity]);

    const canViewGbPositions = useMemo(() => {
        const hasGb10Address = !!gb10Link;
        return hasGb10Address && hasPermission('ACCOUNT_VIEW') && entityType.isDeliverableClient;
    }, [gb10Link, entityType]);

    const { canViewMarginRequirements, canManageInstruments } = useMemo(() => {
        return {
            canViewMarginRequirements:
                hasPermission([
                    'ACCOUNT_MANAGEMENT',
                    'ACCOUNT_VIEW',
                    'ACCOUNT_VIEW_CONFIG',
                    'LIMITED_ACCOUNT_MANAGEMENT',
                    'ORDER_MANAGEMENT'
                ]) && entityType.isMarginClient,
            canManageInstruments: hasPermission('INSTRUMENT_MANAGEMENT')
        };
    }, [entityType, hasPermission]);

    const currenciesApi = usePortalSWR<CurrenciesResponse[]>('/api/cashentry/currencies', () =>
        fetchPortalAPI(onFetchJWT, '/api/cashentry/currencies', jwt)
    );

    const instrumentsApi = usePortalSWR<InstrumentResponse[]>('/api/instruments/list', () =>
        fetchPortalAPI(onFetchJWT, '/api/instruments/list?includeInActive=false', jwt)
    );

    const countriesApi = usePortalSWR<CountriesResponse[]>('/api/entity/countries', () =>
        fetchPortalAPI(onFetchJWT, '/api/entity/countries', jwt)
    );

    const instrumentsIncludeRatioApi = usePortalSWR<InstrumentResponse[]>(
        '/api/instruments/list-instruments-page',
        () =>
            fetchPortalAPI(
                onFetchJWT,
                `/api/instruments/list?includeInActive=${canManageInstruments}`,
                jwt,
                {
                    entityId: foundEntity?.entityId || '',
                    includeRatio: canViewMarginRequirements
                },
                'POST'
            )
    );

    const instrumentCurrenciesApi = usePortalSWR<InstrumentCurrenciesResponse[]>('/api/instruments/currencies', () =>
        fetchPortalAPI(onFetchJWT, '/api/instruments/currencies', jwt)
    );

    const balancesApi = usePortalSWR<BalanceResponse[]>('/api/account/balances', () =>
        fetchPortalAPI(
            onFetchJWT,
            foundEntity && '/api/account/balances',
            jwt,
            { entityId: foundEntity?.entityId },
            'POST'
            // !!foundEntity?.permissions?.includes('ACCOUNT_VIEW')
        )
    );

    const vaspApi = usePortalSWR<VaspResponse[]>('/api/vasps/list', () =>
        fetchPortalAPI(onFetchJWT, foundEntity && '/api/vasps/list', jwt, { entityId: foundEntity?.entityId }, 'POST')
    );

    const bankInfoApi = usePortalSWR<BankAccountsResponse[]>('/api/bank-info', () =>
        fetchPortalAPI(onFetchJWT, foundEntity && '/api/bank-info', jwt, { entityId: foundEntity?.entityId }, 'POST')
    );

    const walletInfoApi = usePortalSWR<WalletsResponse[]>('/api/wallet', () =>
        fetchPortalAPI(onFetchJWT, foundEntity && '/api/wallet', jwt, { entityId: foundEntity?.entityId }, 'POST')
    );

    const ssiProfileBankInfoApi = usePortalSWR<SSIProfileBankAccountsResponse[]>('/api/ssi-profile/bank/list', () =>
        fetchPortalAPI(
            onFetchJWT,
            foundEntity && '/api/ssi-profile/bank/list',
            jwt,
            { entityId: foundEntity?.entityId },
            'POST'
        )
    );

    const ssiProfileWalletInfoApi = usePortalSWR<SSIProfileWalletsResponse[]>('/api/ssi-profile/wallet/list', () =>
        fetchPortalAPI(
            onFetchJWT,
            foundEntity && '/api/ssi-profile/wallet/list',
            jwt,
            { entityId: foundEntity?.entityId },
            'POST'
        )
    );

    const linkedEntitiesApi = usePortalSWR<LinkedEntitiesResponse[]>('/api/entity/linked-entities', () =>
        fetchPortalAPI(
            onFetchJWT,
            foundEntity && '/api/entity/linked-entities',
            jwt,
            { entityId: foundEntity?.entityId },
            'POST'
        )
    );

    const summaryApi = usePortalSWR<SummaryResponse>('/api/account/v2/summary', () =>
        fetchPortalAPI(
            onFetchJWT,
            foundEntity && '/api/account/v2/summary',
            jwt,
            {
                entityId: foundEntity?.entityId,
                tradeDate: summaryHistoricDate,
                isBroker: false,
                direciton: undefined,
                parentEntityName: undefined,
                includeFxOptions: true,
                isDeliverableClient: entityType?.isDeliverableClient,
                includeGbBalances: canViewGbPositions,
                cid: canViewGbPositions ? encodeBase64(gb10Link?.value || '') : undefined
            },
            'POST'
        )
    );

    const marginRequirementsApi = usePortalSWR<MarginRequirementsResponse>('/api/entity/margin-requirements', () =>
        fetchPortalAPI(
            onFetchJWT,
            foundEntity && foundEntity?.attributes?.includes('Margin') && foundEntity?.attributes?.includes('Customer')
                ? '/api/entity/margin-requirements'
                : '',
            jwt,
            {
                entityId: foundEntity?.entityId,
                summaryHistoricDate
            },
            'POST'
        )
    );

    const lastEODApi = usePortalSWR<LastEodResponse>('/api/entity/last-eod', () =>
        fetchPortalAPI(
            onFetchJWT,
            foundEntity && '/api/entity/last-eod',
            jwt,
            { entityId: foundEntity?.entityId },
            'POST'
        )
    );

    const blockchainsApi = usePortalSWR<BlockchainNetworks[]>('/api/whitelist/blockchain-networks', () =>
        fetchPortalAPI(onFetchJWT, '/api/whitelist/blockchain-networks', jwt)
    );

    useEffect(() => {
        summaryApi.mutate();
    }, [entityType, canViewGbPositions, selectedBalancesDirection]);

    useEffect(() => {
        if (jwt) {
            entitiesApi.mutate();
            blockchainsApi.mutate();
            currenciesApi.mutate();
            instrumentsApi.mutate();
            countriesApi.mutate();
        }
    }, [jwt]);

    useEffect(() => {
        if (foundEntity) {
            instrumentsIncludeRatioApi.mutate();
        }
    }, [foundEntity]);

    const value = useMemo<SsoContext>(
        () => ({
            jwt,
            vaspApi,
            entities,
            entityType,
            flattenEntities,
            lastEODApi,
            summaryApi,
            balancesApi,
            bankInfoApi,
            walletInfoApi,
            currenciesApi,
            blockchainsApi,
            instrumentsApi,
            instrumentsIncludeRatioApi,
            linkedEntitiesApi,
            instrumentCurrenciesApi,
            ssiProfileBankInfoApi,
            marginRequirementsApi,
            ssiProfileWalletInfoApi,
            countriesApi,
            summaryHistoricDate,
            selectedBalancesDirection,
            setSummaryHistoricDate,
            setSelectedBalancesDirection,
            isEntitiesLoading,
            canRequestDeposit: hasPermission('REQUEST_DEPOSIT'),
            canRequestWithdrawal: hasPermission('REQUEST_WITHDRAWAL'),
            permissions: foundEntity?.permissions || [],
            depositEnabled,
            withdrawalEnabled,
            isSSOEnabled,
            isLoadingSSO,
            foundEntity,
            hasPermission,
            onFetchJWT,
            accountType,
            setAccountType,
            isSpecificAccountSelected
        }),
        [
            jwt,
            vaspApi,
            entities,
            entityType,
            flattenEntities,
            lastEODApi,
            summaryApi,
            balancesApi,
            bankInfoApi,
            walletInfoApi,
            currenciesApi,
            blockchainsApi,
            instrumentsApi,
            instrumentsIncludeRatioApi,
            linkedEntitiesApi,
            instrumentCurrenciesApi,
            ssiProfileBankInfoApi,
            marginRequirementsApi,
            ssiProfileWalletInfoApi,
            countriesApi,
            summaryHistoricDate,
            selectedBalancesDirection,
            setSummaryHistoricDate,
            setSelectedBalancesDirection,
            isEntitiesLoading,
            depositEnabled,
            withdrawalEnabled,
            isSSOEnabled,
            isLoadingSSO,
            foundEntity,
            hasPermission,
            onFetchJWT,
            accountType,
            setAccountType,
            isSpecificAccountSelected
        ]
    );

    return <SSOContext.Provider value={value}>{props.children}</SSOContext.Provider>;
};

export async function fetchPortalAPI<T>(
    onFetchJWT: () => Promise<T>,
    endpoint?: string,
    jwt?: string,
    requestPayload = {},
    mode: 'GET' | 'POST' = 'GET'
): Promise<T> {
    if (!endpoint || !jwt || !portalURL || !requestPayload) return Promise.resolve(undefined as T);
    try {
        let res;
        Logger({
            title: `Outbound: ${mode} ${endpoint}.`,
            callback: () => {
                if (mode === 'GET') {
                    console.log({ params: requestPayload });
                } else {
                    console.log({ requestPayload });
                }
            }
        });
        if (mode === 'GET') {
            res = await axios.get<T>(portalAPI(endpoint), {
                headers: { Authorization: `Bearer ${jwt}` },
                params: requestPayload
            });
        } else {
            res = await axios.post<T>(portalAPI(endpoint), requestPayload, {
                headers: { Authorization: `Bearer ${jwt}` }
            });
        }
        Logger({
            title: `Inbound: ${mode} ${endpoint}.`,
            callback: () => {
                console.log({ res });
            }
        });
        return res.data; // return just the array
    } catch (error: any) {
        Logger({
            title: `Inbound: Error ${error?.code} on ${endpoint}.`,
            callback: () => {
                console.log({ error });
            }
        });

        if (error?.response?.status === 401) {
            logToServer('error', `Unauthenticated access to ${endpoint}. JWT may be invalid or expired.`);
            Logger({
                title: 'Session has expired, attempting to refresh Session or redirect to login',
                callback: () => {}
            });
            const credentials = store.getState().auth.user;
            if (!credentials) {
                Logger({
                    title: 'No auth token found, redirecting to login...',
                    callback: () => {}
                });
                window.location.href = '/login';
            } else {
                try {
                    const jwt = await onFetchJWT();
                    if (jwt) {
                        Logger({
                            title: 'JWT refreshed successfully',
                            callback: () => {}
                        });
                    } else {
                        Logger({
                            title: 'Failed to refresh JWT, redirecting to login...',
                            callback: () => {}
                        });
                        window.location.href = '/login';
                    }
                } catch (error) {
                    Logger({
                        title: 'Failed to refresh JWT, redirecting to login...',
                        callback: () => {}
                    });
                    window.location.href = '/login';
                }
            }
        }
        return Promise.reject(error);
    }
}

export const usePortalSWR = <T,>(
    key: string,
    fetcher: () => Promise<T>,
    options?: SWRConfiguration<T>
): SWRResponse<T> =>
    useSWR<T>(
        portalURL ? key : null,
        () => {
            return fetcher();
        },
        options || fetchOnce
    );

export function flattenEntityChildren(entities: EntityResponse[]): EntityResponse[] {
    return entities.reduce((acc: EntityResponse[], entity) => {
        const { children, ...restParentEntity } = entity;
        if (children.length) {
            return [...acc, restParentEntity, ...flattenEntityChildren(entity.children)] as EntityResponse[];
        }
        return [...acc, entity];
    }, []);
}

export function encodeBase64(value: string) {
    const bytes = new TextEncoder().encode(value);
    let binary = '';

    bytes.forEach((b) => {
        binary += String.fromCharCode(b);
    });

    return btoa(binary);
}
